import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { AccountInfoState } from "../../account-info-header/models/AccountInfoState";
import { selectInfo } from "../../account-info-header/reducers";
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement } from "../../authorize/reducers";
import { ModifiedPayeeNames } from "../../modified-first-parties/models/FirstParties";
import { selectModifiedFirstPartyPayeesFormatted } from "../../modified-first-parties/reducers";
import { EAuthInfo } from "../../shared/models/EAuthInfo";
import { PaidForDetails, TrustFieldsRequestInfo } from "../../shared/models/TrustFieldsRequestInfo";
import { selectAcknowledgementDateTime, selectEAuthInfo } from "../../shared/reducers";
import { StatusViewService } from "../../shared/services/status-view.service";
import { isStandingInstruction } from "../../shared/types/StandingInstruction";
import { TransactionsTableColumn } from "../../shared/types/TransactionTableColumn";
import { distributionReasons, payIncomeDict, transactionCodes } from "../../shared/utilities/Constants";
import { StandingAuthCheckActions } from "../../standing-authorizations/actions";
import { StandingAuthCheckInfo } from "../../standing-authorizations/models/StandingAuthCheckInfo";
import { CheckStandingAuthDetails, StandingAuthorizationForm } from "../../standing-authorizations/models/standingAuthorizationForm";
import { selectIsFromTableOrView, selectStagedStandingAuthForm, selectStandingAuthCheckInfo, selectStandingAuthForm } from "../../standing-authorizations/reducers";
import { CheckActions, TransactionFormActions } from "../actions";
import { PtrsInfo, isCashieringTransaction } from "../models";
import { CheckAcknowledgeRequest } from "../models/check/CheckAcknowledgeRequest";
import { CheckData, PayeeDetails } from "../models/check/CheckData";
import { CheckInfo } from "../models/check/CheckInfo";
import { CheckInputRequest } from "../models/check/CheckInputRequest";
import { CheckInquiryRequest } from "../models/check/CheckInquiryRequest";
import { CheckInquiryResponse } from "../models/check/CheckInquiryResponse";
import { CheckVerifyRequest, KeyValue } from "../models/check/CheckVerifyRequest";
import { CheckTransactionDetails, ChecksExtraPrompts, TransactionForm } from "../models/form/TransactionForm";
import { EAuthModelCustomer } from "../models/internal-transfer/EAuthModelCustomer";
import { ServiceError, ServiceErrorWithList } from "../models/shared/ServiceError";
import { selectCheckInfo, selectCurrentTransaction, selectIsFromEdit, selectIsFromEditSubmittedTransaction, selectPtrsInfo, selectStagedTransactionForm, selectTransactionForm } from "../reducers";
import { CheckService } from "../services/check.service";
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";
import { selectInitReleaseTransactionInContext, selectInitReleaseTransactionsByDraftIdCallSuccess } from "../../initiate-and-release/reducers";
import { DatePipe } from "@angular/common";
import { CheckHelpers } from "./check.helpers";

@Injectable()
export class CheckEffects {
  constructor(
    private actions$: Actions,
    private checkService: CheckService,
    private statusViewService: StatusViewService,
    private store: Store,
    private datepipe: DatePipe
  ) { }

  callUseSloaInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.standingAuthSelected),
      switchMap(action =>
        this.checkService.callUseSloaProfile(action.payload).pipe(
          map((checkInputResponse: CheckData) => {
            return CheckActions.inputCallSuccessful({ checkInputResponse });
          }),
          catchError((err: ServiceError) =>
            of(CheckActions.inputCallFailed(err))
          )
        )
      )
    )
  );

  callOneTimeCheckInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callOneTimeInput),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEdit)
      ]),
      switchMap(([_, accountInfo, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }
        const checkInputRequest: CheckInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          returnAction: 'accountDetails',
          zAction: 'oneTimeCheck',
          bsloaEnabled: false,
          bsloatransaction: false
        };

        // Circumvents bad API requests by returning an action that does nothing instead of making the call
        if (checkInputRequest.subAccountId === '' || checkInputRequest.masterAccountId === '') {
          return of(TransactionFormActions.accountEmpty());
        }

        return this.checkService.callOneTimeCheckInput(checkInputRequest).pipe(
          map((checkInputResponse) => {
            return CheckActions.inputCallSuccessful({ checkInputResponse });
          }),
          catchError((err: ServiceError) =>
            of(CheckActions.inputCallFailed(err))
          )
        );
      })
    )
  );

  callOneTimeCheckInputSilent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callOneTimeInputSilent),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEdit)
      ]),
      switchMap(([_, accountInfo, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }
        const checkInputRequest: CheckInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          returnAction: 'accountDetails',
          zAction: 'oneTimeCheck',
          bsloaEnabled: false,
          bsloatransaction: false
        };

        // Circumvents bad API requests by returning an action that does nothing instead of making the call
        if (checkInputRequest.subAccountId === '' || checkInputRequest.masterAccountId === '') {
          return of(TransactionFormActions.accountEmpty());
        }

        return this.checkService.callOneTimeCheckInput(checkInputRequest).pipe(
          map((checkInputResponse) => {
            // Return a different success action that doesn't trigger full reducer updates
            return CheckActions.inputCallSuccessfulSilent({ checkInputResponse });
          }),
          catchError((err: ServiceError) =>
            of(CheckActions.inputCallFailed(err))
          )
        );
      })
    )
  )

  callOneTimeCheckInputforCreateStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthCheckActions.callOneTimeInput),
      concatLatestFrom(() => [
        this.store.select(selectInfo)
        //add edit selector here
      ]),
      switchMap(([action, accountInfo]) => {
        const checkInputRequest: CheckInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          returnAction: 'accountDetails',
          zAction: action.isCheckSloa ? 'createSLOACheck' : 'oneTimeCheck',
          bsloaEnabled: false,
          bsloatransaction: false,
          isCheckSloa: action.isCheckSloa
        };
        return this.checkService.callOneTimeCheckInput(checkInputRequest).pipe(
          map((checkInputResponse) => {
            return StandingAuthCheckActions.inputCallSuccessful({ checkInputResponse });
          }),
          catchError((err: ServiceError) =>
            of(StandingAuthCheckActions.inputCallFailed(err))
          )
        );
      })
    )
  );

  callRecurringCheckInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callRecurringInput),
      concatLatestFrom(() => [
        this.store.select(selectInfo)
      ]),
      switchMap(([_, accountInfo]) => {
        const checkInputRequest: CheckInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          returnAction: 'accountDetails',
          zAction: 'recurringCheck',
          bsloaEnabled: false,
          bsloatransaction: false,
          isCheckSloa: false
        };

        // Circumvents bad API requests by returning an action that does nothing instead of making the call
        if (checkInputRequest.subAccountId === '' || checkInputRequest.masterAccountId === '') {
          return of(TransactionFormActions.accountEmpty());
        }

        return this.checkService.callRecurringCheckInput(checkInputRequest).pipe(
          map((checkInputResponse) => {
            return CheckActions.inputCallSuccessful({ checkInputResponse });
          }),
          catchError((err: ServiceError) =>
            of(CheckActions.inputCallFailed(err))
          )
        );
      })
    )
  );

  callOnetimeCheckVerify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callOnetimeCheckVerify),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectTransactionForm),
        this.store.select(selectCheckInfo),
        this.store.select(selectIsFromEditSubmittedTransaction),
        this.store.select(selectCurrentTransaction),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectPtrsInfo),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess)
      ]),
      exhaustMap(([_,
        accountInfo,
        transactionForm,
        checkInfo,
        isFromEditSubmittedTransaction,
        currentTransaction,
        checksModifiedPayeesList,
        hasMmInitiateAccess,
        ptrsInfo,
        initReleaseTransactionInContext,
        draftCallSuccess
      ]) => {
        var request = this.createCheckVerifyRequest(
          accountInfo,
          transactionForm,
          checkInfo,
          false,
          isFromEditSubmittedTransaction,
          currentTransaction,
          checksModifiedPayeesList,
          hasMmInitiateAccess,
          ptrsInfo,
          initReleaseTransactionInContext,
          draftCallSuccess
        );
        return this.checkService.callOneTimeCheckVerify(request).pipe(
          map((checkVerifyResponse: CheckData) => {
            if (!checkVerifyResponse.hasError) {           
              return CheckActions.verifyCallSuccessful({ checkVerifyResponse });
            } else {
              return CheckActions.verifyCallFinishedWithError({ checkVerifyResponse });
            }
          }),
          catchError((err: ServiceErrorWithList) =>
            of(CheckActions.verifyCallFailed(err))
          )
        );
      })
    )
  );

  callOnetimeCheckVerifyForStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthCheckActions.callVerify),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthForm),
        this.store.select(selectStandingAuthCheckInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectIsFromTableOrView)
      ]),
      exhaustMap(([
        _,
        accountInfo,
        standingAuthForm,
        checkInfo,
        checksModifiedPayeesList,
        hasMmInitiateAccess,
        initReleaseTransactionInContext,
        isEdit
      ]) => {
        var request = this.createCheckVerifyRequestForStandingAuth(
          accountInfo,
          standingAuthForm,
          checkInfo,
          checksModifiedPayeesList,
          hasMmInitiateAccess,
          initReleaseTransactionInContext,
          isEdit
        );
        return this.checkService.callOneTimeCheckVerify(request).pipe(
          map((checkVerifyResponse: CheckData) => {
            if (!checkVerifyResponse.hasError) {
              return StandingAuthCheckActions.verifyCallSuccessful({ checkVerifyResponse });
            } else {
              return StandingAuthCheckActions.verifyCallFinishedWithError({ checkVerifyResponse });
            }
          }),
          catchError((err: ServiceErrorWithList) =>
            of(StandingAuthCheckActions.verifyCallFailed(err))
          )
        );
      })
    )
  );

  callRecurringCheckVerify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callRecurringCheckVerify),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectTransactionForm),
        this.store.select(selectCheckInfo),
        this.store.select(selectIsFromEditSubmittedTransaction),
        this.store.select(selectCurrentTransaction),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectPtrsInfo),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess)
      ]),
      exhaustMap(([
        _,
        accountInfo,
        transactionForm,
        checkInfo,
        isFromEditSubmittedTransaction,
        currentTransaction,
        checksModifiedPayeesList,
        hasMmInitiateAccess,
        ptrsInfo,
        initReleaseTransactionInContext,
        draftCallSuccess
      ]) => {
        var request = this.createCheckVerifyRequest(
          accountInfo,
          transactionForm,
          checkInfo,
          true,
          isFromEditSubmittedTransaction,
          currentTransaction,
          checksModifiedPayeesList,
          hasMmInitiateAccess,
          ptrsInfo,
          initReleaseTransactionInContext,
          draftCallSuccess
        );

        return this.checkService.callRecurringCheckVerify(request).pipe(
          map((checkVerifyResponse: CheckData) => {
            if (!checkVerifyResponse.hasError) {
              return CheckActions.verifyCallSuccessful({ checkVerifyResponse });
            } else {
              return CheckActions.verifyCallFinishedWithError({ checkVerifyResponse });
            }
          }),
          catchError((err: ServiceErrorWithList) =>
            of(CheckActions.verifyCallFailed(err))
          )
        );
      })
    )
  );

  callOnetimeCheckAcknowledge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callOnetimeAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectCheckInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectPtrsInfo),
        this.store.select(selectEAuthInfo),
        this.store.select(selectIsFromEditSubmittedTransaction),
        this.store.select(selectCurrentTransaction),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess)
      ]),
      exhaustMap(([
        _,
        accountInfo,
        transactionForm,
        checkInfo,
        acknowledgementConsentDateTime,
        ptrsInfo,
        eAuthInfo,
        isFromEditSubmittedTransaction,
        currentTransaction,
        checksModifiedPayeesList,
        draftCallSuccess
      ]) => {
        var request = this.createCheckAcknowledgeRequest(
          accountInfo,
          transactionForm,
          checkInfo,
          acknowledgementConsentDateTime,
          false,
          ptrsInfo,
          eAuthInfo,
          isFromEditSubmittedTransaction,
          currentTransaction,
          checksModifiedPayeesList,
          draftCallSuccess
        );
        return this.checkService.callOneTimeCheckAcknowledge(request).pipe(
          map((checkAcknowledgeResponse: CheckData) => {
            return CheckActions.acknowledgeCallSuccessful({ checkAcknowledgeResponse });
          }),
          catchError((err: ServiceErrorWithList) =>
            of(CheckActions.acknowledgeCallFailed(err))
          )
        );
      })
    )
  );

  callRecurringCheckAcknowledge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callRecurringAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectCheckInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectPtrsInfo),
        this.store.select(selectEAuthInfo),
        this.store.select(selectIsFromEditSubmittedTransaction),
        this.store.select(selectCurrentTransaction),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess)
      ]),
      exhaustMap(([
        _,
        accountInfo,
        transactionForm,
        checkInfo,
        acknowledgementConsentDateTime,
        ptrsInfo,
        eAuthInfo,
        isFromEditSubmittedTransaction,
        currentTransaction,
        checksModifiedPayeesList,
        draftCallSuccess
      ]) => {
        var request = this.createCheckAcknowledgeRequest(
          accountInfo,
          transactionForm,
          checkInfo,
          acknowledgementConsentDateTime,
          true,
          ptrsInfo,
          eAuthInfo,
          isFromEditSubmittedTransaction,
          currentTransaction,
          checksModifiedPayeesList,
          draftCallSuccess
        );
        return this.checkService.callRecurringCheckAcknowledge(request).pipe(
          map((checkAcknowledgeResponse: CheckData) => {
            return CheckActions.acknowledgeCallSuccessful({ checkAcknowledgeResponse });
          }),
          catchError((err: ServiceErrorWithList) =>
            of(CheckActions.acknowledgeCallFailed(err))
          )
        );
      })
    )
  );

  callOnetimeAcknowledgeStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthCheckActions.callAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedStandingAuthForm),
        this.store.select(selectStandingAuthCheckInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectEAuthInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectIsFromTableOrView),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectHasAccessReleaseMoneyMovement)
      ]),
      switchMap(([_, accountInfo, form, checkInfo, acknowledgementConsentDateTime, eAuthInfo, checksModifiedPayeesList, initReleaseTransactionInContext, isEdit, hasMmInitiateAccess, hasMMReleaseAccess]) => {
        var request = this.createCheckAcknowledgeRequestStandingAuth(
          accountInfo,
          form,
          checkInfo,
          acknowledgementConsentDateTime,
          eAuthInfo,
          checksModifiedPayeesList,
          initReleaseTransactionInContext,
          isEdit,
          hasMmInitiateAccess,
          hasMMReleaseAccess
        );
        return this.checkService.callOneTimeCheckAcknowledge(request).pipe(
          map((response: CheckData) => {
            return StandingAuthCheckActions.acknowledgeCallSuccessful({ checkAcknowledgeResponse: response });
          }),
          catchError((err: ServiceErrorWithList) =>
            of(StandingAuthCheckActions.acknowledgeCallFailed(err))
          )
        );
      })
    )
  );

  callCheckInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckActions.callInquiry),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEditSubmittedTransaction)
      ]),
      switchMap(([action, accountInfoState, isFromEditSubmittedTransaction]) => {
        let isIncoming: boolean = false;
        if (isCashieringTransaction(action.payload) || isStandingInstruction(action.payload)) {
          isIncoming = action.payload.fundsDirection === 'Incoming';
        }
        let payload: CheckInquiryRequest = {
          cashieringRequestId: action.payload.cashieringRqstId,
          cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
          isFromView: !isFromEditSubmittedTransaction,
          isIncoming: isIncoming,
          subAccountId: parseInt(accountInfoState.accountId),
          masterAccountId: accountInfoState.masterAccount,
          isCheckSloa: false
        };

        return this.statusViewService.callCheckInquiry(payload).pipe(
          map((checkInquiryResponse: CheckInquiryResponse) => {
            if (!checkInquiryResponse?.cashieringCheckInputDetails) {
              let err: ServiceError = <ServiceError>{ message: 'Inquiry call failed' }
              return CheckActions.inquiryCallFailed(err);
            }
            return isIncoming ? CheckActions.incomingInquiryCallSuccessful({ checkInquiryResponse }) : CheckActions.inquiryCallSuccessful({ checkInquiryResponse });
          }),
          catchError((err: ServiceError) => of(CheckActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );

  callCheckInquiryForStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthCheckActions.callCheckInquiry),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEditSubmittedTransaction)
      ]),
      switchMap(([action, accountInfoState, isFromEditSubmittedTransaction]) => {
        let isIncoming: boolean = false;
        if (isCashieringTransaction(action.payload) || isStandingInstruction(action.payload)) {
          isIncoming = action.payload.fundsDirection === 'Incoming';
        }
        let payload: CheckInquiryRequest = {
          cashieringRequestId: action.payload.cashieringRqstId,
          cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
          isFromView: !isFromEditSubmittedTransaction,
          isIncoming: isIncoming,
          subAccountId: parseInt(accountInfoState.accountId),
          masterAccountId: accountInfoState.masterAccount,
          isCheckSloa: true
        };

        return this.statusViewService.callCheckInquiry(payload).pipe(
          map((checkInquiryResponse: CheckInquiryResponse) => {
            //TODO Need to map with appr. model
            if (!checkInquiryResponse?.cashieringCheckInputDetails) {
              let err: ServiceError = <ServiceError>{ message: 'Inquiry call failed' }
              return StandingAuthCheckActions.inquiryCallFailed(err);
            }
            return StandingAuthCheckActions.inquiryCallSuccessful({ checkInquiryResponse });
          }),
          catchError((err: ServiceError) => of(CheckActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );

  createCheckRequest(
    accountInfo: AccountInfoState,
    checkInfo: CheckInfo | StandingAuthCheckInfo,
    transactionDetails: CheckTransactionDetails | CheckStandingAuthDetails,
    hyphenate: boolean,
    isUseSloa: boolean,
    isEdit: boolean,
    isFirstPartyModifiedForRequest: boolean
  ) {
    let out = {
      ...CheckHelpers.accountDisplayRecord(accountInfo, hyphenate),
      ...CheckHelpers.accountFields(accountInfo),
      ...CheckHelpers.checkFields(checkInfo),
      ...CheckHelpers.feeDetail(checkInfo),
      ...CheckHelpers.memoLine2Fields(transactionDetails, checkInfo),
      ...CheckHelpers.payeeLineFields(transactionDetails, checkInfo, isUseSloa, isEdit),
      ...CheckHelpers.taxCheckFields(checkInfo),
      ...CheckHelpers.transactionFields(transactionDetails),
      ...CheckHelpers.withdrawlFields(transactionDetails, checkInfo),

      actvTweIn: false,
      isEdit: isEdit,
      IsFirstPartyModified: isFirstPartyModifiedForRequest, // TODO - comment in old UI: Not mapped, used when isTransactionFromSLOAOrSLOA is true
      sloaPopupSelected: isUseSloa,
    };
    return out;
  };

  createCheckVerifyRequest(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    checkInfo: CheckInfo,
    isRecurring: boolean,
    isEdit: boolean,
    currentTransaction: TransactionsTableColumn | undefined,
    checksModifiedPayeesList: ModifiedPayeeNames[],
    hasMmInitiateAccess: boolean,
    ptrsInfo: PtrsInfo,
    InRTransactionInContext: DraftListAttributes,
    isDraftCallSuccess: boolean
  ): CheckVerifyRequest {
    let transactionDetails = form.transactionDetails as CheckTransactionDetails;
    let extraPrompts = form.extraPrompts as ChecksExtraPrompts;
    const isUseSloa = extraPrompts.useStandingAuth === 'yes' || extraPrompts.useBundledStandingAuth === 'yes' ? true : false
    let processDate = new Date(transactionDetails.processDate!);
    let isInternational: boolean = transactionDetails.recipient === 'thirdPartyInternational';
    let isThirdPartyRecipient: boolean = transactionDetails.recipient !== 'firstParty';
    let isFirstPartyModifiedForRequest = !isDraftCallSuccess ? this.getIsFirstPartyModified(checkInfo, transactionDetails, isUseSloa, checksModifiedPayeesList) : transactionDetails.modifiedPayeeCheckbox ?? false;
    let modifiedNames: (string | undefined)[] = [transactionDetails.modifiedPayeeName1, transactionDetails.modifiedPayeeName2, transactionDetails.modifiedPayeeName3];
    let isFromEditTable: boolean = form.prefilledFlow.name === 'edit-table';
    let request: CheckVerifyRequest = {
      ...this.createCheckRequest(accountInfo, checkInfo, transactionDetails, true, isUseSloa, isEdit, isFirstPartyModifiedForRequest),

      // Parameters differ from create standing auth versions
      ...CheckHelpers.addressFields(isThirdPartyRecipient, isInternational, transactionDetails, isUseSloa, checkInfo),
      ...CheckHelpers.amountFields(transactionDetails),
      ...CheckHelpers.cashieringFields(isEdit, currentTransaction),
      ...CheckHelpers.distributionReasonFields(transactionDetails.taxWithholdingsFormGroup, checkInfo, transactionDetails),
      ...CheckHelpers.processDateFields(processDate),
      ...CheckHelpers.taxTransactionFields(transactionDetails.taxWithholdingsFormGroup),
      bsloatransaction: extraPrompts.useStandingAuth === 'bundled',

      ...CheckHelpers.verifyFields(InRTransactionInContext),

      payeeDetails: isFirstPartyModifiedForRequest ?
        this.mapFirstModifiedPayee(modifiedNames ?? [], checkInfo, transactionDetails.payeeName ?? '', transactionDetails.modifiedPayeeCheckbox ?? false, checksModifiedPayeesList, isFromEditTable) :
        this.getPayeeDetailsFrom(checkInfo.defaultPayee, checkInfo.payeeDetails),
    }
    if (isUseSloa) {
      request.Recipientusesloa = checkInfo.mailAddressDetails;
      request.cashieringSecondReqId = isEdit ? parseInt(currentTransaction?.CashieringRqstSecondId!) : checkInfo.cashieringRequestSecondId;
      request.cashieringReqId = checkInfo.cashieringRequestId;
      request.CheckPayeeTypeCode = checkInfo.checkPayeeTypeCode;
      request.MailAddressTypeCode = checkInfo.mailAddressTypeCode;
    }
    if (isRecurring) {
      request.frequency = transactionDetails.recurringFrequency;
      request.frequencyDescription = checkInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)?.description ?? '';
      request.payIncome = transactionDetails.payIncome ? new KeyValue(transactionDetails.payIncome, payIncomeDict[transactionDetails.payIncome]) : new KeyValue();
      request.recurringTxn = {
        payIncome: {
          code: transactionDetails.payIncome ?? ''
        }
      }
      if (transactionDetails.secondProcessDate) {
        const secondProcessDate = new Date(transactionDetails.secondProcessDate!);
        request.recurringTxn.process2ndDateDD = secondProcessDate.getDate();
        request.recurringTxn.process2ndDateMM = secondProcessDate.getMonth() + 1;
        request.recurringTxn.process2ndDateYYYY = secondProcessDate.getFullYear();
      }
      if (transactionDetails.endDate) {
        const endDate = new Date(transactionDetails.endDate!);
        request.recurringTxn.endDateDD = endDate.getDate();
        request.recurringTxn.endDateMM = endDate.getMonth() + 1;
        request.recurringTxn.endDateYYYY = endDate.getFullYear();
      }
    }

    // Map additional I&R fields to save in Draft
    if (hasMmInitiateAccess) {
      request.notNAState = checkInfo.isNonNaState;
      request.rmdEligible = accountInfo.classifications.isRmdEligible;
      request.isFromSLOA = isUseSloa;
      request.stateCd = checkInfo.stateCd;
      request.AssignToUserId = checkInfo.assignToUserId;
      request.stateTypeInd = checkInfo.stateTypeIndicator;
      request.memoLine1CustomerId = checkInfo.memoLine1CustomerId;
      request.sloaRecipient = checkInfo.defaultRecipient;

      if (accountInfo.classifications.isPtrs) {
        request.isTrust = accountInfo.classifications.isPtrs;
        request.paidFor = this.getPaidForContact(ptrsInfo, transactionDetails);
        request.trustIncmPct = transactionDetails.trustIncomePercent?.replace('%', '');
        request.trustPrinPct = transactionDetails.trustPrincipalPercent?.replace('%', '');
        request.trustTaxCode = transactionDetails.trustTaxCode;
        request.trustTaxCategory = transactionDetails.trustTaxCategory;
        request.tranMemo = transactionDetails.trustComment;
      }
    }

    return request;
  }

  createCheckAcknowledgeRequest(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    checkInfo: CheckInfo,
    acknowledgementConsentDateTime: string,
    isRecurring: boolean,
    ptrsInfo: PtrsInfo,
    eAuthInfo: EAuthInfo,
    isEditFromSubmittedTransaction: boolean,
    currentTransaction: TransactionsTableColumn | undefined,
    checksModifiedPayeesList: ModifiedPayeeNames[],
    isDraftCallSuccess: boolean
  ): CheckAcknowledgeRequest {
    let transactionDetails = form.transactionDetails as CheckTransactionDetails;
    let extraPrompts = form.extraPrompts as ChecksExtraPrompts;
    const isUseSloa = (extraPrompts.useStandingAuth === 'yes' || extraPrompts.useBundledStandingAuth === 'yes') ? true : false
    let processDate = new Date(checkInfo.processDate.split('-').join('/')); //needs to be in 'MM/dd/yyyy' format for Safari to compile into Date object
    let isInternational: boolean = transactionDetails.recipient === 'thirdPartyInternational';
    let isThirdPartyRecipient: boolean = transactionDetails.recipient !== 'firstParty';
    let selectedEauthCustomer = eAuthInfo.isAccountHolderSelected ? checkInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.filter(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]) : [];
    let isFirstPartyModifiedForRequest = !isDraftCallSuccess ? this.getIsFirstPartyModified(checkInfo, transactionDetails, isUseSloa, checksModifiedPayeesList) : transactionDetails.modifiedPayeeCheckbox ?? false;
    let isFromEditTable: boolean = form.prefilledFlow.name === 'edit-table';
    let request: CheckAcknowledgeRequest = {
      ...this.createCheckRequest(accountInfo, checkInfo, transactionDetails, false, isUseSloa, isEditFromSubmittedTransaction, isFirstPartyModifiedForRequest),

      // Parameters differ from create standing auth versions
      ...CheckHelpers.addressFields(isThirdPartyRecipient, isInternational, transactionDetails, isUseSloa, checkInfo),
      ...CheckHelpers.amountFields(transactionDetails),
      ...CheckHelpers.cashieringFields(isEditFromSubmittedTransaction, currentTransaction),
      ...CheckHelpers.distributionReasonFields(transactionDetails.taxWithholdingsFormGroup, checkInfo, transactionDetails),
      ...CheckHelpers.processDateFields(processDate),
      ...CheckHelpers.taxTransactionFields(transactionDetails.taxWithholdingsFormGroup),
      bsloatransaction: extraPrompts.useStandingAuth === 'bundled',

      ...CheckHelpers.acknowledgeFields(checkInfo, eAuthInfo, acknowledgementConsentDateTime),

      payeeDetails: isFirstPartyModifiedForRequest ?
        this.mapFirstModifiedPayee([transactionDetails.modifiedPayeeName1, transactionDetails.modifiedPayeeName2, transactionDetails.modifiedPayeeName3], checkInfo, transactionDetails.payeeName ?? '', transactionDetails.modifiedPayeeCheckbox ?? false, checksModifiedPayeesList, isFromEditTable) :
        this.getPayeeDetailsFrom(checkInfo.defaultPayee, checkInfo.payeeDetails),
      // Specific to this function
      CustomerList: selectedEauthCustomer,
      CustomerListSLOA: this.mapStandingAuthEauthList(checkInfo, eAuthInfo),
      submitAndCreateSLOA: checkInfo.isSubmitAndCreate,
      trustFieldsRequestInfo: this.mapTransAddFields(accountInfo.classifications.isPtrs, transactionDetails, ptrsInfo, accountInfo.accountId),
    }
    if (isUseSloa) {
      request.Recipientusesloa = checkInfo.mailAddressDetails;
      request.cashieringSecondReqId = isEditFromSubmittedTransaction ? parseInt(currentTransaction?.CashieringRqstSecondId!) : checkInfo.cashieringRequestSecondId;
      request.cashieringReqId = checkInfo.cashieringRequestId;
      request.CheckPayeeTypeCode = checkInfo.checkPayeeTypeCode;
      request.MailAddressTypeCode = checkInfo.mailAddressTypeCode;
    }
    if (isRecurring) {
      request.frequency = transactionDetails.recurringFrequency;
      request.frequencyDescription = checkInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)?.description ?? '';
      request.payIncome = transactionDetails.payIncome ? new KeyValue(transactionDetails.payIncome, payIncomeDict[transactionDetails.payIncome]) : new KeyValue();
      request.recurringTxn = {
        payIncome: {
          code: transactionDetails.payIncome ?? ''
        }
      }
      if (transactionDetails.secondProcessDate) {
        const secondProcessDate = new Date(transactionDetails.secondProcessDate!);
        request.recurringTxn.process2ndDateDD = secondProcessDate.getDate();
        request.recurringTxn.process2ndDateMM = secondProcessDate.getMonth() + 1;
        request.recurringTxn.process2ndDateYYYY = secondProcessDate.getFullYear();
      }
      if (transactionDetails.endDate) {
        const endDate = new Date(transactionDetails.endDate!);
        request.recurringTxn.endDateDD = endDate.getDate();
        request.recurringTxn.endDateMM = endDate.getMonth() + 1;
        request.recurringTxn.endDateYYYY = endDate.getFullYear();
      }
    }
    return request;
  }

  createCheckVerifyRequestForStandingAuth(
    accountInfo: AccountInfoState,
    form: StandingAuthorizationForm,
    checkInfo: StandingAuthCheckInfo,
    checksModifiedPayeesList: ModifiedPayeeNames[],
    hasMmInitiateAccess: boolean,
    InRTransactionInContext: DraftListAttributes,
    isEdit: boolean
  ): CheckVerifyRequest {
    let standingAuthorizationDetails = form.standingAuthorizationDetails as CheckStandingAuthDetails;
    let isInternational: boolean = standingAuthorizationDetails.recipient === 'thirdPartyInternational';
    let isThirdPartyRecipient: boolean = standingAuthorizationDetails.recipient !== 'firstParty';
    let isFirstPartyModifiedForRequest = this.getIsFirstPartyModified(checkInfo, standingAuthorizationDetails, false, checksModifiedPayeesList);
    let modifiedNames: (string | undefined)[] = [standingAuthorizationDetails.modifiedPayeeName1, standingAuthorizationDetails.modifiedPayeeName2, standingAuthorizationDetails.modifiedPayeeName3];
    let request: CheckVerifyRequest = {
      ...this.createCheckRequest(accountInfo, checkInfo, standingAuthorizationDetails, true, false, isEdit, isFirstPartyModifiedForRequest),

      // Parameters differ from transaction versions
      ...CheckHelpers.addressFields(isThirdPartyRecipient, isInternational, standingAuthorizationDetails),
      ...CheckHelpers.amountFields(),
      ...CheckHelpers.cashieringFields(isEdit, checkInfo),
      ...CheckHelpers.distributionReasonFields(standingAuthorizationDetails, checkInfo),
      ...CheckHelpers.processDateFields(checkInfo),
      ...CheckHelpers.taxTransactionFields(standingAuthorizationDetails),

      ...CheckHelpers.createStandingAuthFields(form, standingAuthorizationDetails),
      ...CheckHelpers.verifyFields(InRTransactionInContext),

      payeeDetails: isFirstPartyModifiedForRequest ?
        this.mapFirstModifiedPayee(modifiedNames ?? [], checkInfo, standingAuthorizationDetails.payeeName ?? '', standingAuthorizationDetails.modifiedPayeeCheckbox ?? false, checksModifiedPayeesList, false) :
        this.getPayeeDetailsFrom(checkInfo.defaultPayee, checkInfo.payeeDetails),
    }
    
    //This ensures the recipient address is not changes on edit of standing authorization, it uses the name and id from the inquiry call
    if(isEdit && checkInfo.mailAddressDetails && checkInfo.mailAddressDetails.length > 0){
        request.payeeDetails = {
          payeeName1: checkInfo.mailAddressDetails[0]?.mailName,
          payeeName2: checkInfo.mailAddressDetails[1]?.mailName,
          payeeName3: checkInfo.mailAddressDetails[2]?.mailName,
          customerId1: checkInfo.mailAddressDetails[0]?.mailNameCustomerId,
          customerId2: checkInfo.mailAddressDetails[1]?.mailNameCustomerId,
          customerId3: checkInfo.mailAddressDetails[2]?.mailNameCustomerId,
        }
      }
    // Map additional I&R fields to save in Draft
    if (hasMmInitiateAccess) {
      request.notNAState = checkInfo.isNonNaState;
      request.rmdEligible = accountInfo.classifications.isRmdEligible;
      request.stateCd = checkInfo.stateCd;
      request.AssignToUserId = checkInfo.assignToUserId;
      request.stateTypeInd = checkInfo.stateTypeIndicator;
      request.memoLine1CustomerId = checkInfo.memoLine1CustomerId;
      request.sloaRecipient = checkInfo.defaultRecipient;
      if (!!InRTransactionInContext.draftId) {
        request.IsFirstPartyModified = !!InRTransactionInContext.draftObject.IsFirstPartyModified,
          request.payeeDetails = {
            payeeName1: InRTransactionInContext.draftObject.payeeDetails?.payeeName1 ?? '',
            payeeName2: InRTransactionInContext.draftObject.payeeDetails?.payeeName2 ?? '',
            payeeName3: InRTransactionInContext.draftObject.payeeDetails?.payeeName3 ?? '',
            customerId1: +(InRTransactionInContext.draftObject.payeeDetails?.customerId1 ?? 0),
            customerId2: +(InRTransactionInContext.draftObject.payeeDetails?.customerId2 ?? 0),
            customerId3: +(InRTransactionInContext.draftObject.payeeDetails?.customerId3 ?? 0),
          }
      }

      

      if (accountInfo.classifications.isPtrs) {
        request.paidFor = new PaidForDetails();
        request.trustIncmPct = checkInfo.trustIncmPct;
        request.trustPrinPct = checkInfo.trustPrinPct;
        request.trustTaxCode = '';
        request.tranMemo = checkInfo.tranMemo;
      }
    }
    return request;
  }

  createCheckAcknowledgeRequestStandingAuth(
    accountInfo: AccountInfoState,
    form: StandingAuthorizationForm,
    checkInfo: StandingAuthCheckInfo,
    acknowledgementConsentDateTime: string,
    eAuthInfo: EAuthInfo,
    checksModifiedPayeesList: ModifiedPayeeNames[],
    initReleaseTransactionInContext: DraftListAttributes,
    isEdit: boolean,
    hasMmInitiateAccess: boolean,
    hasMMReleaseAccess: boolean
  ): CheckAcknowledgeRequest {
    const standingAuthorizationDetails = form.standingAuthorizationDetails as CheckStandingAuthDetails;
    const isInternational: boolean = standingAuthorizationDetails.recipient === 'thirdPartyInternational';
    const isThirdPartyRecipient: boolean = standingAuthorizationDetails.recipient !== 'firstParty';
    let isFirstPartyModifiedForRequest = this.getIsFirstPartyModified(checkInfo, standingAuthorizationDetails, false, checksModifiedPayeesList);
    let selectedEauthSigners = eAuthInfo.isAccountHolderSelected ?
      eAuthInfo.selectedAccountHolder
        .map(customerId => checkInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === customerId))
        .filter(customer => customer !== undefined) as EAuthModelCustomer[] : [];
    const request: CheckAcknowledgeRequest = {
      ...this.createCheckRequest(accountInfo, checkInfo, standingAuthorizationDetails, false, false, isEdit, isFirstPartyModifiedForRequest),

      // Parameters differ from transaction versions
      ...CheckHelpers.addressFields(isThirdPartyRecipient, isInternational, standingAuthorizationDetails),
      ...CheckHelpers.amountFields(),
      ...CheckHelpers.cashieringFields(isEdit, checkInfo),
      ...CheckHelpers.distributionReasonFields(standingAuthorizationDetails, checkInfo),
      ...CheckHelpers.processDateFields(checkInfo),
      ...CheckHelpers.taxTransactionFields(standingAuthorizationDetails),

      ...CheckHelpers.acknowledgeFields(checkInfo, eAuthInfo, acknowledgementConsentDateTime),
      ...CheckHelpers.createStandingAuthFields(form, standingAuthorizationDetails),

      payeeDetails: isFirstPartyModifiedForRequest ?
        this.mapFirstModifiedPayee([standingAuthorizationDetails.modifiedPayeeName1, standingAuthorizationDetails.modifiedPayeeName2, standingAuthorizationDetails.modifiedPayeeName3], checkInfo, standingAuthorizationDetails.payeeName ?? '', standingAuthorizationDetails.modifiedPayeeCheckbox ?? false, checksModifiedPayeesList, false) :
        this.getPayeeDetailsFrom(checkInfo.defaultPayee, checkInfo.payeeDetails),
      // Specific to this function
      CustomerList: [],
      CustomerListSLOA: selectedEauthSigners,
      formType: 'CheckSloa',
      trustFieldsRequestInfo: null,
    }

    //This ensures the recipient address is not changes on edit of standing authorization, it uses the name and id from the inquiry call
    if(isEdit && checkInfo.mailAddressDetails && checkInfo.mailAddressDetails.length > 0){
        request.payeeDetails = {
          payeeName1: checkInfo.mailAddressDetails[0]?.mailName,
          payeeName2: checkInfo.mailAddressDetails[1]?.mailName,
          payeeName3: checkInfo.mailAddressDetails[2]?.mailName,
          customerId1: checkInfo.mailAddressDetails[0]?.mailNameCustomerId,
          customerId2: checkInfo.mailAddressDetails[1]?.mailNameCustomerId,
          customerId3: checkInfo.mailAddressDetails[2]?.mailNameCustomerId,
        }
      }

    if ((hasMMReleaseAccess || hasMmInitiateAccess) && !!initReleaseTransactionInContext.draftId) {
      request.IsFirstPartyModified = !!initReleaseTransactionInContext.draftObject.IsFirstPartyModified,
        request.payeeDetails = {
          payeeName1: initReleaseTransactionInContext.draftObject.payeeDetails?.payeeName1 ?? '',
          payeeName2: initReleaseTransactionInContext.draftObject.payeeDetails?.payeeName2 ?? '',
          payeeName3: initReleaseTransactionInContext.draftObject.payeeDetails?.payeeName3 ?? '',
          customerId1: +(initReleaseTransactionInContext.draftObject.payeeDetails?.customerId1 ?? 0),
          customerId2: +(initReleaseTransactionInContext.draftObject.payeeDetails?.customerId2 ?? 0),
          customerId3: +(initReleaseTransactionInContext.draftObject.payeeDetails?.customerId3 ?? 0),
        }
    }
    return request;
  }

  mapTransAddFields(isPtrs: boolean, transactionDetails: CheckTransactionDetails, ptrsInfo: PtrsInfo, accountId: string): TrustFieldsRequestInfo | null {
    if (isPtrs) {
      const selectedContact = this.getPaidForContact(ptrsInfo, transactionDetails);

      return {
        accountNumber: accountId,
        comment: transactionDetails.trustComment!,
        incomePercent: parseFloat(transactionDetails.trustIncomePercent!),
        principalPercent: parseFloat(transactionDetails.trustPrincipalPercent!),
        settlementDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
        taxCode: transactionDetails.trustTaxCode!,
        tradeDate: new Date().toISOString(),
        transactionCode: transactionCodes.checkTransactionCode,
        paidForContact: selectedContact
      }
    }
    return null;
  }

  getPaidForContact(ptrsInfo: PtrsInfo, transactionDetails: CheckTransactionDetails) {
    const paidFor: PaidForDetails = ptrsInfo.paidFor.individuals.find(individual =>
      (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) &&
      (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails();

    return paidFor;
  }

  /**
   * @description The payeeDetails are mapped differently in the verify request for edit flow. This method maps them to the defaultPayees to match legacy behavior
   * @param defaultPayees the list of defaultPayees for the transaction
   * @returns the payeeDetails for the verify request
   */
  getPayeeDetailsFrom(defaultPayees: string[], checkInfoPayees: PayeeDetails): PayeeDetails {
    let payeeDetails: PayeeDetails = {
      payeeName1: defaultPayees[0] ?? '',
      payeeName2: defaultPayees[1] ?? '',
      payeeName3: defaultPayees[2] ?? '',
      customerId1: checkInfoPayees?.customerId1 ?? 0,
      customerId2: checkInfoPayees?.customerId2 ?? 0,
      customerId3: checkInfoPayees?.customerId3 ?? 0
    }
    return payeeDetails;
  }

  mapFirstModifiedPayee(
    modifiedNames: (string | undefined)[],
    { payee, payeeDetails, useConstructedPayee }: { payee: string[], payeeDetails: PayeeDetails, useConstructedPayee: boolean },
    payeeName: string,
    useModifiedPayeeNameChecked: boolean,
    checksModifiedPayeesList: ModifiedPayeeNames[],
    isFromEditTable: boolean): PayeeDetails {
    if (isFromEditTable) {
      return {
        ...payeeDetails,
        payeeName1: payee[0] ?? '',
        payeeName2: payee[1] ?? '',
        payeeName3: payee[2] ?? '',
      }
    } else if (useModifiedPayeeNameChecked) {
      return {
        ...payeeDetails,
        payeeName1: modifiedNames[0] ?? '',
        payeeName2: modifiedNames[1] ?? '',
        payeeName3: modifiedNames[2] ?? '',
      }
    } else {
      const delimiter = useConstructedPayee ? ' ' : ' & ';
      const payeeNameFormatted = payeeName?.endsWith('(Pending)') ? payeeName?.slice(0, -10) : payeeName;
      const selectedModifiedPayee = checksModifiedPayeesList.find(mp => [mp.name1, mp.name2, mp.name3].filter(mp => !!mp?.trim()).join(delimiter) === payeeNameFormatted);
      return {
        ...payeeDetails,
        payeeName1: selectedModifiedPayee?.name1 ?? '',
        payeeName2: selectedModifiedPayee?.name2 ?? '',
        payeeName3: selectedModifiedPayee?.name3 ?? '',
      }
    }
  }

  getIsFirstPartyModified({ useConstructedPayee, defaultPayee }: { useConstructedPayee: boolean, defaultPayee: string[] },
    details: { modifiedPayeeCheckbox?: boolean, modifiedPayeeName1?: string, modifiedPayeeName2?: string, modifiedPayeeName3?: string, payeeName?: string, payeeType?: string },
    isUseSloa: boolean, modifiedPayeeList: ModifiedPayeeNames[]): boolean {
    if (isUseSloa || details.payeeType !== 'firstParty') {
      return false;
    }

    const delimiter = useConstructedPayee ? ' ' : ' & ';
    const defaultPayees = defaultPayee
      .filter((payee) => !!payee.trim())
      .join(delimiter);
    const selectedPayee = details.modifiedPayeeCheckbox
      ? [
        details.modifiedPayeeName1,
        details.modifiedPayeeName2,
        details.modifiedPayeeName3,
      ]
        .filter((mp) => !!mp?.trim())
        .join(delimiter)
      : details.payeeName;

    if (defaultPayees === selectedPayee) {
      return false;
    }

    const payee = modifiedPayeeList.find(payee => [payee.name1, payee.name2, payee.name3].filter(mp => !!mp?.trim()).join(delimiter) === selectedPayee);
    if (payee) {
      return !payee.isPrimary;
    }
    return true;
  }

  mapStandingAuthEauthList(checkInfo: CheckInfo, eAuthInfo: EAuthInfo): EAuthModelCustomer[] {
    let standingAuthApprovers: EAuthModelCustomer[] = [];

    let selectedEauthCustomer = eAuthInfo.isAccountHolderSelected ?
      checkInfo.eAuthModel.custAcctSLOAAuthorizationsList.eAuthCustomerList.filter(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]) :
      [];

    if (checkInfo.isSubmitAndCreate) {
        standingAuthApprovers = selectedEauthCustomer;
    }

    return standingAuthApprovers;
  }
};
